import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { User, Mail, Phone, MapPin, Lock, Save, Edit3, Download, Loader2, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';

// Loading Button Component
const LoadingButton = ({ loading, children, ...props }) => (
  <button
    {...props}
    disabled={loading}
    className={`flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
      loading
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-orange-500 hover:bg-orange-600 text-white hover:shadow-lg'
    }`}
  >
    {loading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
    {children}
  </button>
);

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [resetMailLoading, setResetMailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
    address_country: 'Türkiye',
    billing_name: '',
    billing_phone: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_zip_code: '',
    billing_country: 'Türkiye'
  });

  useEffect(() => {
    if (user && (!formData.name || !formData.email)) {
      const hasBilling = Boolean(
        user.billing_street || user.billing_city || user.billing_name
      );
      setShowBillingAddress(hasBilling);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address_street: user.address_street || '',
        address_city: user.address_city || '',
        address_state: user.address_state || '',
        address_zip_code: user.address_zip_code || '',
        address_country: user.address_country || 'Türkiye',
        billing_name: user.billing_name || '',
        billing_phone: user.billing_phone || user.phone || '',
        billing_street: user.billing_street || '',
        billing_city: user.billing_city || '',
        billing_state: user.billing_state || '',
        billing_zip_code: user.billing_zip_code || '',
        billing_country: user.billing_country || 'Türkiye'
      });
    }
  }, [user, formData.name, formData.email]); // ESLint uyarısını gidermek için

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile(formData);
      setIsEditing(false);
      toast.success('Profil başarıyla güncellendi');
    } catch (error) {
      toast.error('Profil güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const hasBilling = Boolean(
      user.billing_street || user.billing_city || user.billing_name
    );
    setShowBillingAddress(hasBilling);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      address_street: user.address_street || '',
      address_city: user.address_city || '',
      address_state: user.address_state || '',
      address_zip_code: user.address_zip_code || '',
      address_country: user.address_country || 'Türkiye',
      billing_name: user.billing_name || '',
      billing_phone: user.billing_phone || user.phone || '',
      billing_street: user.billing_street || '',
      billing_city: user.billing_city || '',
      billing_state: user.billing_state || '',
      billing_zip_code: user.billing_zip_code || '',
      billing_country: user.billing_country || 'Türkiye'
    });
    setIsEditing(false);
  };

  const handleAutoFill = async () => {
    if (!navigator.geolocation) {
      toast.error('Tarayıcınız konum özelliğini desteklemiyor');
      return;
    }

    toast.loading('Konumunuz alınıyor...', { id: 'geolocation' });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocoding API (Nominatim - ücretsiz)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=tr`
          );
          
          if (!response.ok) {
            throw new Error('Adres bilgisi alınamadı');
          }
          
          const data = await response.json();
          
          if (data.display_name) {
            const addressParts = data.display_name.split(', ');
            
            setFormData(prev => ({
              ...prev,
              address_street: addressParts[0] || '',
              address_city: addressParts[addressParts.length - 3] || '',
              address_state: addressParts[addressParts.length - 4] || '',
              address_country: 'Türkiye'
            }));
            
            toast.success('Adres bilgileri otomatik dolduruldu!', { id: 'geolocation' });
          } else {
            throw new Error('Adres bilgisi bulunamadı');
          }
        } catch (error) {
          console.error('Geocoding hatası:', error);
          toast.error('Adres bilgisi alınamadı', { id: 'geolocation' });
        }
      },
      (error) => {
        console.error('Konum hatası:', error);
        toast.error('Konumunuz alınamadı', { id: 'geolocation' });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 w-full min-w-0">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6 max-w-full">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                <User className="h-7 w-7 sm:h-8 sm:w-8 text-orange-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{user.name}</h1>
                <p className="text-sm text-gray-600 break-all">{user.email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsEditing(!isEditing)}
              className="flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors shrink-0"
            >
              <Edit3 className="h-4 w-4" />
              <span>{isEditing ? 'İptal' : 'Düzenle'}</span>
            </button>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 max-w-full overflow-hidden">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Profil Bilgileri</h2>
            {isEditing && (
              <button
                type="button"
                onClick={handleAutoFill}
                className="flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <Download className="h-4 w-4 shrink-0" />
                <span className="text-center">Konumdan otomatik doldur</span>
              </button>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ad Soyad
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full max-w-full box-border pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>

            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-orange-500" />
                Teslimat adresi
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Address Street */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adres
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    name="address_street"
                    value={formData.address_street}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Mahalle, sokak, bina no"
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Şehir
                </label>
                <input
                  type="text"
                  name="address_city"
                  value={formData.address_city}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İlçe
                </label>
                <input
                  type="text"
                  name="address_state"
                  value={formData.address_state}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              {/* Zip Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posta Kodu
                </label>
                <input
                  type="text"
                  name="address_zip_code"
                  value={formData.address_zip_code}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ülke
                </label>
                <input
                  type="text"
                  name="address_country"
                  value={formData.address_country}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={() => setShowBillingAddress((v) => !v)}
                className="flex w-full items-center justify-between text-left rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <span className="flex items-center gap-2 font-medium text-gray-900">
                  <FileText className="h-5 w-5 text-orange-500" />
                  Fatura adresi {showBillingAddress ? '' : '(isteğe bağlı)'}
                </span>
                {showBillingAddress ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </button>
              <p className="text-sm text-gray-500 mt-2 px-1">
                Fatura adresiniz teslimat adresinden farklıysa buradan girebilirsiniz.
              </p>

              {showBillingAddress && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fatura adı / unvan</label>
                    <input
                      type="text"
                      name="billing_name"
                      value={formData.billing_name}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder={formData.name || 'Ad soyad veya firma unvanı'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fatura telefonu</label>
                    <input
                      type="tel"
                      name="billing_phone"
                      value={formData.billing_phone}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fatura adresi</label>
                    <input
                      type="text"
                      name="billing_street"
                      value={formData.billing_street}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Şehir</label>
                    <input
                      type="text"
                      name="billing_city"
                      value={formData.billing_city}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">İlçe</label>
                    <input
                      type="text"
                      name="billing_state"
                      value={formData.billing_state}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Posta kodu</label>
                    <input
                      type="text"
                      name="billing_zip_code"
                      value={formData.billing_zip_code}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ülke</label>
                    <input
                      type="text"
                      name="billing_country"
                      value={formData.billing_country}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <LoadingButton
                  type="submit"
                  loading={loading}
                  className="flex items-center space-x-2 px-6 py-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Kaydet</span>
                    </>
                  )}
                </LoadingButton>
              </div>
            )}
          </form>
        </div>

        {/* Password Change Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Şifre Değiştir</h2>
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <Lock className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-gray-600">
                Sıfırlama linki yalnızca hesabınıza kayıtlı adrese gider:{' '}
                <strong className="text-gray-900">{user?.email}</strong>
              </p>
              <LoadingButton
                type="button"
                loading={resetMailLoading}
                className="mt-4"
                onClick={async () => {
                  setResetMailLoading(true);
                  try {
                    await authAPI.forgotPasswordMe();
                    toast.success(`Sıfırlama linki ${user.email} adresine gönderildi`);
                  } catch (error) {
                    toast.error(error.response?.data?.message || 'E-posta gönderilemedi');
                  } finally {
                    setResetMailLoading(false);
                  }
                }}
              >
                Şifre sıfırlama e-postası gönder
              </LoadingButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;